import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Crown, Check, Loader2, ShieldCheck, ArrowLeft, Sparkles, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { UpgradeNudge } from '../components/UpgradeNudge';
import { PaymentTermsCheck } from '../components/PaymentTermsCheck';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const loadRazorpay = () => new Promise((resolve) => {
  if (window.Razorpay) return resolve(true);
  const s = document.createElement('script');
  s.src = 'https://checkout.razorpay.com/v1/checkout.js';
  s.onload = () => resolve(true);
  s.onerror = () => resolve(false);
  document.body.appendChild(s);
});

const fmt = (amount, isIndia) => (isIndia ? `₹${Number(amount).toLocaleString('en-IN')}` : `$${amount}`);
const BASIC_INCLUDES = ['AI-generated company profile', '1 Quick Bite video/month', 'Video hosting', 'Email signature', 'QR code', 'Private, link-only profile'];

const PlanCard = ({ id, plan, selected, onSelect, isIndia, badge, includes, highlight }) => (
  <button
    type="button"
    onClick={() => onSelect(id)}
    data-testid={`plan-card-${id}`}
    className={`text-left rounded-2xl border p-6 transition-colors flex flex-col ${selected ? 'border-[#C41E3A] bg-[#C41E3A]/10' : 'border-zinc-800 bg-zinc-900/60 hover:border-zinc-600'}`}
  >
    <div className="flex items-center justify-between mb-3 gap-2">
      <span className="text-zinc-300 text-sm flex items-center gap-2">{highlight && <Crown className="w-4 h-4 text-amber-400" />}{plan.name.replace('BrandStage ', '')}</span>
      {badge && <span className="text-xs bg-amber-500/20 text-amber-400 px-2 py-0.5 rounded-full whitespace-nowrap">{badge}</span>}
    </div>
    <div className="text-3xl font-bold text-white" data-testid={`plan-price-${id}`}>{fmt(isIndia ? plan.inr : plan.usd, isIndia)}</div>
    <p className="text-zinc-500 text-xs mt-1 mb-4">{plan.duration_months} months{isIndia ? ' + GST' : ''}</p>
    <ul className="space-y-1.5 mt-auto">
      {includes.map((f) => (
        <li key={f} className="text-zinc-400 text-xs flex items-start gap-2"><Check className="w-3.5 h-3.5 text-emerald-400 shrink-0 mt-0.5" />{f}</li>
      ))}
    </ul>
  </button>
);

const AddonRow = ({ addon, checked, owned, onToggle, isIndia }) => (
  <label
    data-testid={`addon-row-${addon.key}`}
    className={`flex items-start gap-4 rounded-xl border p-4 transition-colors ${owned ? 'opacity-60 cursor-not-allowed border-zinc-800' : 'cursor-pointer'} ${checked ? 'border-[#C41E3A] bg-[#C41E3A]/10' : 'border-zinc-800 bg-zinc-900/60 hover:border-zinc-600'}`}
  >
    <input
      type="checkbox"
      className="mt-1 accent-[#C41E3A] w-4 h-4"
      checked={checked}
      disabled={owned}
      onChange={() => onToggle(addon.key)}
      data-testid={`addon-checkbox-${addon.key}`}
    />
    <div className="flex-1">
      <div className="flex items-center justify-between gap-4">
        <span className="text-white font-medium">{addon.name}</span>
        <span className="text-white font-semibold" data-testid={`addon-price-${addon.key}`}>+{fmt(isIndia ? addon.price_inr : addon.price_usd, isIndia)}</span>
      </div>
      {addon.description && <p className="text-zinc-500 text-sm mt-1">{addon.description}</p>}
      {owned && <p className="text-emerald-400 text-xs mt-2 flex items-center gap-1"><Check className="w-3 h-3" /> Already active</p>}
    </div>
  </label>
);

const SummaryRow = ({ label, value, strong, testId }) => (
  <div className={`flex justify-between gap-4 ${strong ? 'text-white font-bold text-lg pt-3 border-t border-zinc-800' : 'text-zinc-400 text-sm'}`}>
    <span>{label}</span><span data-testid={testId} className="whitespace-nowrap">{value}</span>
  </div>
);

export default function PricingPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [pricing, setPricing] = useState(null);
  const [addons, setAddons] = useState([]);
  const [access, setAccess] = useState({ plan_tier: null, active_addons: [] });
  const [plan, setPlan] = useState(searchParams.get('plan') || 'basic_1y');
  const [selected, setSelected] = useState([]);
  const [country, setCountry] = useState('india');
  const [coupon, setCoupon] = useState('');
  const [paying, setPaying] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);

  useEffect(() => {
    axios.get(`${API_URL}/api/payments/pricing`).then((r) => setPricing(r.data)).catch(() => toast.error('Failed to load pricing'));
    axios.get(`${API_URL}/api/addons`).then((r) => setAddons(r.data.addons || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (!user) return;
    if (user.country && user.country.toLowerCase() !== 'india') setCountry('international');
    axios.get(`${API_URL}/api/me/addons`, { withCredentials: true }).then((r) => {
      setAccess(r.data);
      if (r.data.plan_tier === 'basic' && !searchParams.get('plan')) setPlan('addons');
    }).catch(() => {});
  }, [user]);

  const isIndia = country === 'india';
  const gstRate = isIndia ? (pricing?.gst_rate || 0) : 0;
  const hasBasic = access.plan_tier === 'basic';
  const showAddons = plan === 'basic_6m' || plan === 'basic_1y' || plan === 'addons';
  const chosenAddons = showAddons ? addons.filter((a) => selected.includes(a.key)) : [];
  const addonPrice = (a) => (isIndia ? a.price_inr : a.price_usd);

  const base = useMemo(() => {
    if (!pricing) return 0;
    const planPrice = plan === 'addons' ? 0 : (isIndia ? pricing.products[plan].inr : pricing.products[plan].usd);
    return planPrice + chosenAddons.reduce((sum, a) => sum + addonPrice(a), 0);
  }, [pricing, plan, chosenAddons, isIndia]); // eslint-disable-line react-hooks/exhaustive-deps
  const gst = Math.round(base * gstRate / 100);
  const offer = access.upgrade_offer;
  const nudgeValue = plan === 'addons' ? (offer?.current_value_inr || 0) + base : base;
  const allAccessInr = pricing?.products.all_access_1y.inr || 0;
  const showNudge = isIndia && showAddons && allAccessInr > 0 && nudgeValue >= Math.round(allAccessInr * 5 / 6);

  const toggleAddon = (key) => setSelected((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));

  const checkout = async () => {
    if (!user) { navigate('/login?redirect=/pricing'); return; }
    if (plan === 'addons' && chosenAddons.length === 0) { toast.error('Select at least one feature'); return; }
    setPaying(true);
    try {
      if (!(await loadRazorpay())) throw new Error('Failed to load payment gateway');
      const { data } = await axios.post(`${API_URL}/api/payments/create-order`, {
        product_type: plan,
        country,
        addons: chosenAddons.map((a) => a.key),
        coupon_code: coupon.trim() || null,
        terms_accepted: termsAccepted,
      }, { withCredentials: true });
      const rzp = new window.Razorpay({
        key: data.razorpay_key_id,
        amount: data.amount,
        currency: data.currency,
        name: 'BrandStage',
        description: data.product_name,
        order_id: data.order_id,
        prefill: data.prefill,
        theme: { color: '#C41E3A' },
        handler: async (resp) => {
          try {
            await axios.post(`${API_URL}/api/payments/verify`, resp, { withCredentials: true });
            toast.success('Payment successful! Your plan is now active.');
            navigate('/dashboard');
          } catch {
            toast.error('Payment verification failed. Please contact support.');
          }
        },
        modal: { ondismiss: () => setPaying(false) },
      });
      rzp.open();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || 'Failed to start payment');
      setPaying(false);
    }
  };

  if (!pricing) {
    return <div className="min-h-screen bg-zinc-950 flex items-center justify-center"><Loader2 className="w-8 h-8 text-[#C41E3A] animate-spin" /></div>;
  }

  const allAccessIncludes = ['Everything in Basic', '10 Quick Bite videos/month', 'Professional editing on request', ...addons.map((a) => a.name)];

  return (
    <div className="min-h-screen bg-zinc-950 text-white" data-testid="pricing-page">
      <div className="max-w-6xl mx-auto px-6 pt-28 pb-16">
        <Link to="/dashboard" className="inline-flex items-center gap-2 text-zinc-500 hover:text-white text-sm mb-8" data-testid="pricing-back-link">
          <ArrowLeft className="w-4 h-4" /> Back
        </Link>
        <div className="flex flex-wrap items-end justify-between gap-6 mb-10">
          <div>
            <h1 className="text-4xl sm:text-5xl font-bold tracking-tight mb-3">Choose how you grow</h1>
            <p className="text-zinc-400 text-base max-w-2xl">Start with the Basic platform and add the Pro features you need, or take everything with All-Access.</p>
          </div>
          <select
            value={country}
            onChange={(e) => setCountry(e.target.value)}
            data-testid="pricing-country-select"
            className="bg-zinc-900 border border-zinc-800 rounded-full px-4 py-2.5 text-sm text-zinc-300 focus:outline-none"
          >
            <option value="india">India (INR)</option>
            <option value="international">International (USD)</option>
          </select>
        </div>

        {access.plan_tier === 'all_access' && (
          <div className="mb-8 rounded-xl border border-emerald-500/30 bg-emerald-500/10 p-4 text-emerald-300 text-sm flex items-center gap-2" data-testid="all-access-active-banner">
            <Sparkles className="w-4 h-4" /> Your current plan already includes every feature.
          </div>
        )}

        <div className="grid lg:grid-cols-[1fr_360px] gap-10">
          <div>
            <div className={`grid gap-4 ${hasBasic ? 'sm:grid-cols-2 xl:grid-cols-4' : 'sm:grid-cols-3'}`} data-testid="plan-cards">
              {hasBasic && (
                <button
                  type="button"
                  onClick={() => setPlan('addons')}
                  data-testid="plan-card-addons"
                  className={`text-left rounded-2xl border p-6 transition-colors ${plan === 'addons' ? 'border-[#C41E3A] bg-[#C41E3A]/10' : 'border-zinc-800 bg-zinc-900/60 hover:border-zinc-600'}`}
                >
                  <span className="text-zinc-300 text-sm flex items-center gap-2"><Plus className="w-4 h-4" /> Add to my plan</span>
                  <p className="text-zinc-500 text-xs mt-3">You already have Basic. Just pick the features you want below.</p>
                </button>
              )}
              <PlanCard id="basic_6m" plan={pricing.products.basic_6m} selected={plan === 'basic_6m'} onSelect={setPlan} isIndia={isIndia} includes={BASIC_INCLUDES} />
              <PlanCard id="basic_1y" plan={pricing.products.basic_1y} selected={plan === 'basic_1y'} onSelect={setPlan} isIndia={isIndia} includes={BASIC_INCLUDES} />
              <PlanCard id="all_access_1y" plan={pricing.products.all_access_1y} selected={plan === 'all_access_1y'} onSelect={setPlan} isIndia={isIndia} badge="Everything" includes={allAccessIncludes} highlight />
            </div>

            {showAddons && (
              <div className="mt-10" data-testid="addons-list">
                <h2 className="text-base md:text-lg font-semibold mb-1">Add Pro features</h2>
                <p className="text-zinc-500 text-sm mb-4">Each feature is valid for 12 months from purchase.</p>
                <div className="space-y-3">
                  {addons.length === 0 && <p className="text-zinc-500" data-testid="addons-empty">No add-on features available right now.</p>}
                  {addons.map((a) => (
                    <AddonRow key={a.key} addon={a} checked={selected.includes(a.key)} owned={access.active_addons.includes(a.key)} onToggle={toggleAddon} isIndia={isIndia} />
                  ))}
                </div>
              </div>
            )}
          </div>

          <aside className="rounded-2xl border border-zinc-800 bg-zinc-900/60 p-6 h-fit lg:sticky lg:top-24" data-testid="order-summary">
            <h2 className="text-lg font-semibold mb-5">Order summary</h2>
            <div className="space-y-2">
              {plan !== 'addons' && (
                <SummaryRow label={pricing.products[plan].name} value={fmt(isIndia ? pricing.products[plan].inr : pricing.products[plan].usd, isIndia)} testId="summary-plan" />
              )}
              {chosenAddons.map((a) => (
                <SummaryRow key={a.key} label={a.name} value={fmt(addonPrice(a), isIndia)} testId={`summary-addon-${a.key}`} />
              ))}
              {isIndia && <SummaryRow label={`GST (${gstRate}%)`} value={fmt(gst, isIndia)} testId="summary-gst" />}
              <SummaryRow label="Total" value={fmt(base + gst, isIndia)} strong testId="summary-total" />
            </div>
            {showNudge && (
              <div className="mt-5">
                <UpgradeNudge currentValue={nudgeValue} allAccessPrice={allAccessInr} onSwitch={() => { setPlan('all_access_1y'); setSelected([]); }} testId="pricing-upgrade-nudge" />
              </div>
            )}
            <input
              value={coupon}
              onChange={(e) => setCoupon(e.target.value.toUpperCase())}
              placeholder="Promo code (applied at checkout)"
              data-testid="pricing-coupon-input"
              className="w-full mt-5 bg-zinc-950 border border-zinc-800 rounded-lg px-4 py-2.5 text-sm uppercase placeholder-zinc-600 focus:outline-none focus:border-zinc-600"
            />
            <div className="mt-4"><PaymentTermsCheck checked={termsAccepted} onChange={setTermsAccepted} /></div>
            <button
              type="button"
              onClick={checkout}
              disabled={paying || base === 0 || (user && !termsAccepted)}
              data-testid="pricing-checkout-btn"
              className="w-full mt-4 bg-[#C41E3A] hover:bg-[#A01830] disabled:opacity-50 text-white py-3 rounded-full font-semibold transition-colors flex items-center justify-center gap-2"
            >
              {paying && <Loader2 className="w-4 h-4 animate-spin" />}
              {user ? `Pay ${fmt(base + gst, isIndia)}` : 'Log in to continue'}
            </button>
            <p className="text-zinc-600 text-xs mt-4 flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5" /> Secure payment powered by Razorpay</p>
          </aside>
        </div>

        {/* Also Available Section */}
        <div className="mt-16 border-t border-zinc-800 pt-12" data-testid="also-available-section">
          <h2 className="text-xl font-bold mb-2">Also Available</h2>
          <p className="text-zinc-500 text-sm mb-8">Premium services that complement your BrandStage profile</p>
          
          <div className="grid sm:grid-cols-2 gap-6">
            {/* Quick Bite Videos */}
            <div className="bg-zinc-900/60 border border-zinc-800 rounded-2xl p-6">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-lg bg-[#C41E3A]/20 flex items-center justify-center">
                  <svg className="w-5 h-5 text-[#C41E3A]" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg>
                </div>
                <h3 className="font-semibold">Quick Bite Video Recordings</h3>
              </div>
              <p className="text-zinc-400 text-sm mb-4 leading-relaxed">
                Record short-form professional videos directly from your browser. Share them instantly via BrandStage links across LinkedIn, email, and more.
              </p>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between text-zinc-400"><span>Basic plan</span><span className="text-white font-medium">1 video / month</span></div>
                <div className="flex justify-between text-zinc-400"><span>All-Access plan</span><span className="text-white font-medium">10 videos / month</span></div>
                <div className="flex justify-between text-zinc-400"><span>Professional editing</span><span className="text-white font-medium">On request (All-Access)</span></div>
              </div>
            </div>

            {/* Leadership Interviews */}
            <div className="bg-zinc-900/60 border border-zinc-800 rounded-2xl p-6">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center">
                  <svg className="w-5 h-5 text-purple-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" /></svg>
                </div>
                <h3 className="font-semibold">Leadership Interviews</h3>
              </div>
              <p className="text-zinc-400 text-sm mb-4 leading-relaxed">
                Professionally produced long-form interviews with your leadership team. Custom scripted, branded, and delivered — ideal for investor presentations, team introductions, and thought leadership content.
              </p>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between text-zinc-400"><span>Pricing</span><span className="text-white font-medium">Custom quote based on scope</span></div>
                <div className="flex justify-between text-zinc-400"><span>Includes</span><span className="text-white font-medium">Script, production & branding</span></div>
              </div>
              <a
                href="mailto:brandstagevideo@gmail.com?subject=Leadership%20Interview%20Inquiry&body=Hi%20BrandStage%20team%2C%0A%0AI%20am%20interested%20in%20a%20leadership%20interview%20for%20my%20organisation.%20Could%20you%20share%20a%20quote%20based%20on%20our%20requirements%3F%0A%0AThank%20you"
                className="inline-flex items-center gap-2 mt-4 text-sm font-medium text-[#C41E3A] hover:text-white transition-colors"
                data-testid="leadership-interview-cta"
              >
                Get a quote →
              </a>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
}
